import { Button, Modal, notify, Text, Flex } from '@wyz/ui';
import { useState } from 'react';
import { Trans } from '~/common/context';
import {
  ContainerSearchEditableGrid,
  type TableRow,
} from '~/purchase/components';

import '@glideapps/glide-data-grid/dist/index.css';
import { useManageCustomerContainerOrders } from '~/purchase/api';
import { IUserCustomer } from '~/common/types';
import { IValidateEansPayload } from '~/purchase/types';
import { usePurchaseErrorTranslation } from '~/common/hooks';

type ContainerSearchPasteModalProps = {
  onClose: () => void;
  onSuccess: (data: IValidateEansPayload['requests']) => void;
  targetCustomer: IUserCustomer;
  containerType: IValidateEansPayload['type'];
};

function ContainerSearchPasteModal({
  onClose,
  onSuccess,
  containerType,
  targetCustomer,
}: ContainerSearchPasteModalProps) {
  const [gridData, setGridData] = useState<TableRow[]>([]);
  const { resolveErrorKey } = usePurchaseErrorTranslation();

  const manageContainerOrders = useManageCustomerContainerOrders({
    context: { customerId: targetCustomer.id },
    validateEans: {
      enabled: true,
    },
  });

  const handleSubmit = () => {
    // as requested by business, we ignore empty rows
    const filledRows = gridData.filter(
      (row) => row.ean || row.quantity,
    ) as IValidateEansPayload['requests'];
    if (filledRows.length === 0) {
      notify.error(<Trans i18nKey='app.common.validations.error_message' />, {
        id: 'toast-error-container-paste-empty',
      });
      return;
    }
    manageContainerOrders.validateEans.mutate(
      {
        requests: filledRows,
        type: containerType,
      },
      {
        onSuccess: () => {
          onSuccess(filledRows);
        },
        onError: (response) => {
          notify.error(<Trans i18nKey={resolveErrorKey(response.message)} />, {
            id: 'toast-error-container-paste-validation-failed',
          });
        },
      },
    );
  };

  return (
    <Modal.Container open={true} onClose={onClose} size='lg' backdrop='static'>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.container.order.result.paste_modal.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Text>
          <Trans i18nKey='app.container.order.result.paste_modal.subtitle' />
        </Text>
        <ContainerSearchEditableGrid
          onChange={setGridData}
          errors={
            manageContainerOrders.validateEans.isError
              ? (manageContainerOrders.validateEans.error.errors ?? [])
              : undefined
          }
        />
        <Flex className='space-x-2 mt-3'>
          <Button
            id='cancel-paste'
            variant='secondary'
            onClick={onClose}
            fullWidth
            disabled={manageContainerOrders.validateEans.isLoading}
          >
            <Trans i18nKey='app.common.button.cancel' />
          </Button>
          <Button
            id='submit-paste'
            variant='primary'
            onClick={handleSubmit}
            fullWidth
            isLoading={manageContainerOrders.validateEans.isLoading}
          >
            <Trans i18nKey='app.common.buttons.validate' />
          </Button>
        </Flex>
      </Modal.Body>
    </Modal.Container>
  );
}

export default ContainerSearchPasteModal;
